/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  MIME_TYPES,
  TIMEZONE_WIB,
  TIMEZONE_WIT,
  TIMEZONE_WITA,
} from '@navi-app/constants';
import clsx, { ClassValue } from 'clsx';
import { format, isValid } from 'date-fns';
import { format as formatTz, utcToZonedTime } from 'date-fns-tz';
import { enUS, id } from 'date-fns/locale';
import i18next from 'i18next';
import { twMerge } from 'tailwind-merge';
import { DynamicObject } from './hooks/use-upload-file';

export function cn(...classes: ClassValue[]) {
  return twMerge(clsx(classes));
}

export const isEmpty = (obj: any) =>
  [Object, Array].includes((obj || {}).constructor) &&
  !Object.entries(obj || {}).length;

export const get = (
  obj: any,
  path: string,
  defaultValue = undefined as any
) => {
  const travel = (regexp: any) =>
    String.prototype.split
      .call(path, regexp)
      .filter(Boolean)
      .reduce(
        (res, key) => (res !== null && res !== undefined ? res[key] : res),
        obj
      );
  const result = travel(/[,[\]]+?/) || travel(/[,[\].]+?/);
  return result === undefined || result === obj ? defaultValue : result;
};

export const isBrowserCached = () => {
  if ('caches' in window) {
    caches.match(window.location.href).then(function (response) {
      if (response) {
        return true;
      } else {
        return false;
      }
    });
  } else {
    // Browser does not support the caches API
    return false;
  }
};

const dateFnsLocales = { id, en: enUS };

export const formatDateToLocal = (
  dateValue: string | Date,
  dateFormat: string = 'dd MMM yyyy',
  zone?: typeof TIMEZONE_WIB | typeof TIMEZONE_WITA | typeof TIMEZONE_WIT,
  defaultValue = '-'
) => {
  if (!isValid(new Date(dateValue)) || !dateValue) {
    return defaultValue;
  }
  const lang = i18next?.language || 'id'; // switch to id
  const locale = dateFnsLocales[lang as keyof typeof dateFnsLocales];

  if (zone) {
    const zonedDate = utcToZonedTime(dateValue, zone);
    return format(zonedDate, dateFormat, { locale });
  }

  return format(new Date(dateValue), dateFormat, { locale });
};

export const formatDatePayload = (
  date: string | Date,
  pattern = "yyyy-MM-dd'T'HH:mm:ss'Z'",
  defaultValue = '-'
) => {
  if (!isValid(new Date(date)) || !date) {
    return defaultValue;
  }
  if (date) {
    const zonedDate = utcToZonedTime(date, TIMEZONE_WIB);
    const dateValue = formatTz(zonedDate, pattern, {
      timeZone: TIMEZONE_WIB,
    });
    return dateValue;
  }
  return null;
};

export const cleanObject = (obj: any): any => {
  const cleanedObj: any = {};

  for (const key in obj) {
    if (obj[key] !== null && obj[key] !== undefined && obj[key] !== '') {
      cleanedObj[key] = obj[key];
    }
  }

  return cleanedObj;
};

export const cloneDeep = (obj: any) => JSON.parse(JSON.stringify(obj));

export const compareObjectsAreEqual = (
  obj1: DynamicObject,
  obj2: DynamicObject
) => {
  return JSON.stringify(obj1) === JSON.stringify(obj2);
};

export function toPascalCaseWithSpaces(inputString = '') {
  return inputString
    .replace(/_/g, ' ')
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (match, index) => match.toUpperCase())
    .replace(/\s+/g, ' ');
}

export function isValidUrl(url: string): boolean {
  try {
    const urlObject = new URL(url);
    return urlObject.protocol !== null && urlObject.host !== null;
  } catch (error) {
    return false;
  }
}

export const cleanEmptyKeyObject = (obj: any) => {
  if (obj === null || obj === undefined) {
    return {};
  }
  const cloneObj = structuredClone(obj);
  Object.keys(cloneObj).map((key) => {
    if (
      cloneObj[key] === '' ||
      isEmpty(cloneObj[key]) ||
      cloneObj[key] === null
    ) {
      delete cloneObj[key];
    }
    return key;
  });
  return cloneObj;
};

export const isLengthValid = (text: string, length: number) => {
  return text.length >= length;
};

export const hasUpperCase = (text: string) => {
  return /[A-Z]/.test(text);
};

export const hasNumber = (text: string) => {
  return /[0-9]/.test(text);
};

export const hasSymbol = (text: string) => {
  return /[\W_]/.test(text);
};

export function encodeString(input: string) {
  return btoa(input);
}

export function decodeString(input: string) {
  return atob(input);
}

export function isValidBase64String(input: string) {
  const base64regex =
    /^(?:[A-Za-z0-9+/]{4})*?(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;
  return base64regex.test(input);
}
export const removeWords = (inputString: string, wordsToRemove: string[]) => {
  wordsToRemove?.forEach((word: string) => {
    inputString = inputString?.replace(new RegExp(word, 'gi'), '');
  });
  return inputString?.trim();
};

export const convertMinutesToHours = (minutes: number) => {
  if (typeof minutes !== 'number' || minutes === 0) {
    return '-';
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  const roundedMinutes = Math.floor(remainingMinutes);
  return `${hours || 0} jam ${roundedMinutes || 0} menit`;
};

export const convertToHoursAndMinutes = (minutes: number) => {
  if (typeof minutes !== 'number' || minutes === 0) {
    return { hours: 0, minutes: 0 };
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return {
    hours: hours || 0,
    minutes: remainingMinutes || 0,
  };
};

export const formatStringToNumber = (str: string) =>
  str?.replace(/\./g, '').replace(',', '.');

export const formatConvertToComa = (str: string) =>
  str?.replace(',', '').replace('.', ',');

export const formatConvertToFullStop = (str: string) => str.replace(',', '.');

export const removeLeadingZeros = (value: string) => {
  // Handle cases where the number has a delimiter
  if (value.includes(',') || value.includes('.')) {
    return value.replace(/^0+(\d)/, '$1'); // Keep at least one leading zero if followed by a delimiter
  }
  // If there's no delimiter, just remove all leading zeros
  return value.replace(/^0+/, '') || '0'; // Ensure empty result returns "0"
};

export const formatStringToDecimal = (str: string) => {
  const [before, after] = str.split(',') || ['', ''];
  let decimal = after?.replace(/\./g, ''); // delete thousands separator

  let newValue = removeLeadingZeros(before?.replace(/\./g, '')); // delete thousands separator & leading zeros
  newValue = newValue.replace(/\B(?=(\d{3})+(?!\d))/g, '.'); // re-add formatting thousand separator for returned value

  if (decimal) {
    decimal = decimal.replace(/0+$/, '');
  }
  return decimal ? `${newValue},${decimal}` : newValue;
};

export const formatNumberToString = (
  number = 0,
  maxDecimal = 3,
  isShowDefaultDecimal = false
) =>
  new Intl.NumberFormat('id-ID', {
    minimumFractionDigits: isShowDefaultDecimal ? maxDecimal : 0,
    maximumFractionDigits: maxDecimal,
  }).format(number);

export const formatNumberInput = (num: string) => {
  // Split the number into integer and decimal parts
  const parts = num.split(',');
  let integerPart = parts[0];
  const decimalPart = parts.length > 1 ? ',' + parts[1] : '';

  // Remove any non-digit characters (except for commas)
  integerPart = integerPart.replace(/\D/g, '');

  // Add dot separator for thousands
  integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

  return integerPart + decimalPart;
};

export const getTimeZoneAbbreviation = () => {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const timeZoneAbbreviation =
    timeZone === TIMEZONE_WIB
      ? 'WIB'
      : timeZone === TIMEZONE_WITA
      ? 'WITA'
      : timeZone === TIMEZONE_WIT
      ? 'WIT'
      : timeZone;
  return timeZoneAbbreviation;
};

export const formatDateWithTimeZone = (
  dateStr: string,
  isShowTimezone = true
) => {
  if (dateStr) {
    const date = new Date(dateStr);

    // Check if the date is valid before proceeding
    if (!isValid(date)) {
      return dateStr;
    }

    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const zonedDate = utcToZonedTime(date, timeZone);
    const formattedDate = formatTz(zonedDate, 'dd MMM yyyy, HH:mm', {
      timeZone,
    });

    const timeZoneAbbreviation =
      timeZone === TIMEZONE_WIB
        ? 'WIB'
        : timeZone === TIMEZONE_WITA
        ? 'WITA'
        : timeZone === TIMEZONE_WIT
        ? 'WIT'
        : timeZone;

    const dateResult = `${formattedDate} ${timeZoneAbbreviation}`;
    if (!isShowTimezone || !formattedDate || !timeZoneAbbreviation) {
      return format(date, 'dd MMM yyyy, HH:mm', { locale: id });
    } else {
      return dateResult;
    }
  }
  return dateStr;
};

export const toFixed = (x: any) => {
  if (Math.abs(x) < 1.0) {
    const e = parseInt(x.toString().split('e-')[1]);
    if (e) {
      x *= Math.pow(10, e - 1);
      x = '0.' + new Array(e).join('0') + x.toString().substring(2);
    }
  } else {
    let e = parseInt(x.toString().split('+')[1]);
    if (e > 20) {
      e -= 20;
      x /= Math.pow(10, e);
      x += new Array(e + 1).join('0');
    }
  }
  return x;
};

export const slicedDecimal = (temp: string, decimal = 5) => {
  if (temp) {
    const [before, after] = temp.split('.') || ['', ''];
    const slicedDecimal = after?.slice(0, decimal);

    const calculate = slicedDecimal ? `${before},${slicedDecimal}` : before;

    return calculate;
  }
  return temp;
};

export const convertHoursMinutesToMinutes = (
  hours: number,
  minutes: number
) => {
  return hours * 60 + minutes;
};

export const randomColor = () => {
  const letters = '0123456789ABCDEF';
  let color = '#';
  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
};

export const formattingDecimalInter = (
  num: string | number,
  minDecimal = 0,
  maxDecimal = 100
) => {
  const userLocale = navigator.language || 'id-ID';

  // Ensure the input is treated as a number
  const number =
    typeof num === 'string'
      ? Number(num.replace(/,/g, '')) // Replace commas in the string before converting to number
      : num;

  // Round the number to the maximum decimal places
  const roundedNumber = parseFloat(number.toFixed(maxDecimal));

  // Define locale-based options
  const options = {
    style: 'decimal',
    minimumFractionDigits: minDecimal,
    maximumFractionDigits: maxDecimal,
  };

  // Create a formatter based on the locale
  const formatter = new Intl.NumberFormat(userLocale, options as any);

  // Format the number
  return formatter.format(roundedNumber);
};

export const convertToMinutes = (hours: number, minutes: number) => {
  // Calculate total minutes
  const totalMinutes = Number(hours) * 60 + Number(minutes);
  return totalMinutes;
};

export const convertTimeToFullDate = (time: string) => {
  const date = new Date('2024-08-19T00:00:00.000Z');
  const offset = new Date().getTimezoneOffset();
  const diffHours = Math.floor(Math.abs(offset) / 60);
  const sign = offset <= 0 ? '+' : '-';

  // Split the time string into hours and minutes
  const [hours, minutes] = time.split(':').map(Number);

  // Set the hours and minutes on the Date object
  date.setUTCHours(
    sign === '+' ? hours - diffHours : hours + diffHours,
    minutes,
    0,
    0
  );

  // Get the full date-time string with the current time zone
  const fullDateTimeString = date.toString();

  return fullDateTimeString;
};

export const convertTimeToWIB = (time: string) => {
  const date = new Date('2024-08-19T00:00:00.000Z');
  // Split the time string into hours and minutes
  if (!time) {
    return '';
  }

  const [hours, minutes] = time.split(':').map(Number);

  // Set the hours and minutes on the Date object
  date.setUTCHours(hours, minutes, 0, 0);

  // Convert the date to the Asia/Jakarta time zone (GMT+7)
  const zonedDate = utcToZonedTime(date, TIMEZONE_WIB);

  // Format the date to a string with the desired format
  const formatHourMinutes = formatTz(zonedDate, 'HH:mm', {
    timeZone: TIMEZONE_WIB,
  });

  return formatHourMinutes;
};

export const getDateTime = (time: string) => {
  if (time?.length) {
    const [hours, minutes] = time.split(':').map(Number);

    // Create a new Date object with today's date
    const today = new Date();

    // Set the hours and minutes to the specified time
    return today.setHours(hours, minutes, 0, 0);
  }

  return time;
};

export const getValue = (obj: Record<string, any>, field: string) => {
  const fieldComponents = field.split('.');
  let value: any = obj;

  for (const component of fieldComponents) {
    if (value && Object.prototype.hasOwnProperty.call(value, component)) {
      value = value[component];
    } else {
      return undefined;
    }
  }
  return value;
};

export const timeStringToDate = (timeString: string | undefined) => {
  if (!timeString) {
    return '';
  }
  const [hours, minutes, seconds] = timeString.split(':').map(Number);

  const date = new Date();

  date.setHours(hours);
  date.setMinutes(minutes);
  date.setSeconds(seconds);
  date.setMilliseconds(0); // Reset milliseconds
  date.setUTCHours(hours, minutes, 0, 0); // Adjust for GMT+0700

  return date;
};

export function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export const getTimeInHHMM = (date: Date) => {
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');

  return `${hours}:${minutes}`;
};

export function roundUpToDecimalPlaces(
  value: number,
  decimalPlaces: number = 2
) {
  if (!value) {
    return 0;
  }

  const multiplier = Math.pow(10, decimalPlaces);
  value = parseFloat(value.toFixed(decimalPlaces + 1));

  return Math.ceil(value * multiplier) / multiplier;
}

export function getPercentage(
  value: number,
  total: number,
  decimalPlaces: number = 2
) {
  if (!total) {
    total = 1;
  }

  const calc =
    (roundUpToDecimalPlaces(value, decimalPlaces) /
      roundUpToDecimalPlaces(total, decimalPlaces)) *
    100;

  return roundUpToDecimalPlaces(calc, decimalPlaces);
}

export function formatRoundedNumbertoString(
  value: number,
  decimalPlaces: number = 2
) {
  return formatNumberToString(
    roundUpToDecimalPlaces(value, decimalPlaces),
    decimalPlaces
  );
}

export function getDecimalPlaces(value?: number) {
  if (!value) return 0;
  const numStr = value.toString();

  if (numStr.includes('.')) return numStr.split('.')[1].length;

  return 0;
}

export function fileToBase64(file: File) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      if (reader.result) {
        if (typeof reader.result === 'string') {
          resolve(reader.result.split(',')[1]); // Extract Base64 part
        } else {
          reject(new Error('FileReader result is not a string'));
        }
      } else {
        reject(new Error('FileReader result is null'));
      }
    };
    reader.onerror = (error) => reject(error);
  });
}

export function base64ToBlob(base64: string, mimeType: string) {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length)
    .fill(0)
    .map((_, i) => byteCharacters.charCodeAt(i));
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: mimeType });
}

export function base64ToFile(
  base64: string,
  filename: string,
  mimeType: string
) {
  const blob = base64ToBlob(base64, mimeType);
  return new File([blob], filename, { type: mimeType });
}

const SPREADSHEET_NAME_EXT = /\.(xlsx|xls|xlsm|csv)$/i;

/** Ensures `link.download` gets a visible extension when the API omits it (e.g. "template"). */
function ensureSpreadsheetDownloadFileName(
  fileName: string,
  mimeType: string
): string {
  const n = fileName.trim();
  if (!n) {
    return fileName;
  }
  if (mimeType === MIME_TYPES.XLSX && !SPREADSHEET_NAME_EXT.test(n)) {
    return `${n.replace(/\.+$/, '')}.xlsx`;
  }
  return n;
}

export function downloadBase64File(
  base64Data: string,
  fileName: string,
  mimeType: string
) {
  const byteCharacters = atob(base64Data);
  const byteNumbers = new Array(byteCharacters.length)
    .fill(0)
    .map((_, i) => byteCharacters.charCodeAt(i));
  const byteArray = new Uint8Array(byteNumbers);

  const blob = new Blob([byteArray], { type: mimeType });
  const safeFileName = ensureSpreadsheetDownloadFileName(fileName, mimeType);

  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = safeFileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function clickDownloadLink(
  url: string,
  fileName?: string,
  openInNewTab = false
) {
  const link = document.createElement('a');
  link.href = url;
  if (fileName) {
    link.download = fileName;
  }
  if (openInNewTab) {
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
  }
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function canFetchUrlForDownload(url: string) {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    const parsedUrl = new URL(url, window.location.href);
    if (['blob:', 'data:'].includes(parsedUrl.protocol)) {
      return false;
    }

    return parsedUrl.origin === window.location.origin;
  } catch {
    return false;
  }
}

export async function downloadUrlFile(url?: string, fileName?: string) {
  if (!url) {
    return;
  }

  const safeFileName = fileName?.trim() || url.split('/').pop()?.split('?')[0];

  if (!canFetchUrlForDownload(url)) {
    clickDownloadLink(url, safeFileName, true);
    return;
  }

  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error('Failed to fetch file');
    }

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    clickDownloadLink(objectUrl, safeFileName);
    setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
  } catch {
    clickDownloadLink(url, safeFileName, true);
  }
}

export const convertToCamelCase = (str: string) => {
  if (!str) {
    return str;
  }

  return str
    .replace(/[-_\s]+(.)?/g, (_, c) => (c ? c.toUpperCase() : ''))
    .replace(/^[A-Z]/, (match) => match.toLowerCase());
};

export const convertToTitleCase = (str: string) => {
  return str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
};

export const safeParseJSON = (data: any) => {
  if (typeof data !== 'string') return null;
  try {
    const parsed = JSON.parse(data);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

export const getFullStrSubService = (subService?: string) => {
  let str = '';

  if (subService?.includes('C')) {
    str += 'Customs';
  }

  if (subService?.includes('T')) {
    if (str) {
      str += ', ';
    }
    str += 'Trucking';
  }

  if (subService?.includes('F')) {
    if (str) {
      str += ', ';
    }
    str += 'Freight';
  }

  return str;
};

export function reorderData(
  data: DynamicObject[],
  keyData: string,
  orderConfig: { key: string; order: number }[]
): DynamicObject[] {
  if (!data?.length || !orderConfig?.length) return data;

  // Map config: key -> order
  const configMap = new Map(orderConfig.map(({ key, order }) => [key, order]));

  // Separate items
  const ordered: { order: number; item: DynamicObject }[] = [];
  const leftovers: DynamicObject[] = [];

  for (const item of data) {
    const key = item[keyData];
    const order = configMap.get(key);
    if (order && order > 0) {
      ordered.push({ order, item });
    } else {
      leftovers.push(item);
    }
  }

  // Determine max order index
  const maxIndex = Math.max(
    ...ordered.map((o) => o.order),
    data.length + ordered.length
  );

  // Create result array with empty slots
  const result: (DynamicObject | undefined)[] = Array(maxIndex).fill(undefined);

  // Place ordered items
  for (const { order, item } of ordered) {
    const index = Math.min(order, result.length);
    result[index] = item;
  }

  // Fill in undefined slots with leftover items in order
  let leftoverIndex = 0;
  for (let i = 0; i < result.length; i++) {
    if (!result[i] && leftoverIndex < leftovers.length) {
      result[i] = leftovers[leftoverIndex++];
    }
  }

  // Remove undefineds just in case
  return result.filter(Boolean) as DynamicObject[];
}

export function sanitizePhoneNumberDisplay(value: string | number) {
  if (!value) {
    return value;
  }
  let newValue = String(value);
  newValue = newValue.replace(/[^0-9]/g, '').replace(/^0/, '62');
  return newValue;
}

export function sanitizePhoneNumberPayload(
  value: string | number | undefined,
  keyCountryCode: string,
  keyPhoneNumber: string
) {
  if (!value) {
    return {
      [keyCountryCode]: '',
      [keyPhoneNumber]: '',
    };
  }

  const newValue = String(value);
  return {
    [keyCountryCode]: newValue?.startsWith('62') ? '+62' : '',
    [keyPhoneNumber]: newValue?.replace(/^62/, ''),
  };
}

export const removeDuplicateArr = (arr: any[]) => {
  if (!arr?.length) return [];
  const newArr = [...new Set(arr)];
  return newArr;
};

export const removeDuplicateArrObj = (arr: any[], key: string) => {
  if (!arr?.length) return [];
  const mapper = new Map(arr.map((item) => [item[key], item]));
  return Array.from(mapper.values());
};

// Helper function to get coordinate key with precision
export const getCoordKey = (lat: number, long: number, precision = 6) => {
  return `${lat.toFixed(precision)}|${long.toFixed(precision)}`;
};

export function formatTextListString(
  str: string,
  lastSeparator: string = 'dan'
) {
  if (!str) {
    return '';
  }
  const arr = str.split(',');
  return arr.length > 1
    ? arr.slice(0, -1).join(', ') + ' ' + lastSeparator + ' ' + arr.slice(-1)
    : arr[0];
}
